'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Save, RefreshCw, CheckCircle2, AlertCircle, Info, Loader2, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

const supabase = createClient();

interface Campaign {
  id: string;
  name: string;
}

interface EnrollmentRule {
  id: string;
  checkpoint: string;
  campaign_id: string;
  campaign_name: string;
  is_active: boolean;
  last_enrolled_at?: string | null;
}

const CHECKPOINT_META: Record<string, { label: string; description: string; color: string; bg: string }> = {
  fresh: {
    label: 'Fresh',
    description: 'Lead has no checkpoints — newly synced with no activity recorded',
    color: 'text-sky-700',
    bg: 'bg-sky-50 border-sky-200',
  },
  gmc: {
    label: 'GMC',
    description: 'Lead visited the GMC (General Medical Council) section',
    color: 'text-blue-700',
    bg: 'bg-blue-50 border-blue-200',
  },
  services: {
    label: 'Services',
    description: 'Lead explored available MBBS services',
    color: 'text-violet-700',
    bg: 'bg-violet-50 border-violet-200',
  },
  report: {
    label: 'Report',
    description: 'Lead downloaded or viewed a report',
    color: 'text-amber-700',
    bg: 'bg-amber-50 border-amber-200',
  },
  insight: {
    label: 'Insight',
    description: 'Lead accessed insights or analytics content',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50 border-emerald-200',
  },
  support: {
    label: 'Support',
    description: 'Lead reached out via support channel',
    color: 'text-rose-700',
    bg: 'bg-rose-50 border-rose-200',
  },
};

const CHECKPOINT_ORDER = ['fresh', 'gmc', 'services', 'report', 'insight', 'support'];

export default function EnrollmentRulesClient() {
  const [rules, setRules] = useState<EnrollmentRule[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [localRules, setLocalRules] = useState<Record<string, { campaign_id: string; campaign_name: string; is_active: boolean }>>({});

  const fetchCampaigns = useCallback(async () => {
    const { data } = await supabase
      .from('campaign_drip_sequences')
      .select('campaign_id, campaign_name')
      .order('campaign_name');

    if (data) {
      // Deduplicate by campaign_id
      const seen = new Set<string>();
      const unique: Campaign[] = [];
      for (const row of data) {
        if (!seen.has(row.campaign_id)) {
          seen.add(row.campaign_id);
          unique.push({ id: row.campaign_id, name: row.campaign_name });
        }
      }
      setCampaigns(unique);
    }
  }, []);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('enrollment_rules')
      .select('*')
      .order('checkpoint');

    if (error) {
      toast.error('Failed to load enrollment rules');
    } else if (data) {
      setRules(data);
      const map: Record<string, { campaign_id: string; campaign_name: string; is_active: boolean }> = {};
      for (const r of data) {
        map[r.checkpoint] = {
          campaign_id: r.campaign_id,
          campaign_name: r.campaign_name,
          is_active: r.is_active,
        };
      }
      setLocalRules(map);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchRules();
    fetchCampaigns();
  }, [fetchRules, fetchCampaigns]);

  const handleCampaignChange = (checkpoint: string, campaignId: string) => {
    const campaign = campaigns.find((c) => c.id === campaignId);
    setLocalRules((prev) => ({
      ...prev,
      [checkpoint]: {
        ...prev[checkpoint],
        campaign_id: campaignId,
        campaign_name: campaign?.name ?? '',
      },
    }));
  };

  const handleToggle = (checkpoint: string) => {
    setLocalRules((prev) => ({
      ...prev,
      [checkpoint]: {
        ...prev[checkpoint],
        is_active: !prev[checkpoint]?.is_active,
      },
    }));
  };

  const handleSave = async (checkpoint: string) => {
    const rule = rules.find((r) => r.checkpoint === checkpoint);
    const local = localRules[checkpoint];
    setSaving(checkpoint);

    if (!rule) {
      // Insert new row (e.g. fresh checkpoint not yet in DB)
      const { error } = await supabase
        .from('enrollment_rules')
        .insert({
          checkpoint,
          campaign_id: local.campaign_id,
          campaign_name: local.campaign_name,
          is_active: local.is_active,
        });
      if (error) {
        toast.error(`Failed to save rule for ${checkpoint}: ${error.message}`);
      } else {
        toast.success(`Enrollment rule for ${CHECKPOINT_META[checkpoint]?.label ?? checkpoint} saved`);
        await fetchRules();
      }
      setSaving(null);
      return;
    }

    const { error } = await supabase
      .from('enrollment_rules')
      .update({
        campaign_id: local.campaign_id,
        campaign_name: local.campaign_name,
        is_active: local.is_active,
        updated_at: new Date().toISOString(),
      })
      .eq('id', rule.id);

    if (error) {
      toast.error(`Failed to save rule for ${checkpoint}: ${error.message}`);
    } else {
      toast.success(`Enrollment rule for ${CHECKPOINT_META[checkpoint]?.label ?? checkpoint} saved`);
      await fetchRules();
    }
    setSaving(null);
  };

  const handleSaveAll = async () => {
    setSaving('all');
    let hasError = false;

    for (const checkpoint of CHECKPOINT_ORDER) {
      const rule = rules.find((r) => r.checkpoint === checkpoint);
      const local = localRules[checkpoint];
      if (!local) continue;

      if (!rule) {
        // Insert if row doesn't exist yet
        const { error } = await supabase
          .from('enrollment_rules')
          .insert({
            checkpoint,
            campaign_id: local.campaign_id,
            campaign_name: local.campaign_name,
            is_active: local.is_active,
          });
        if (error) hasError = true;
      } else {
        const { error } = await supabase
          .from('enrollment_rules')
          .update({
            campaign_id: local.campaign_id,
            campaign_name: local.campaign_name,
            is_active: local.is_active,
            updated_at: new Date().toISOString(),
          })
          .eq('id', rule.id);
        if (error) hasError = true;
      }
    }

    if (hasError) {
      toast.error('Some rules failed to save. Please try again.');
    } else {
      toast.success('All enrollment rules saved successfully');
      await fetchRules();
    }
    setSaving(null);
  };

  const isDirty = (checkpoint: string) => {
    const rule = rules.find((r) => r.checkpoint === checkpoint);
    if (!rule) return false;
    const local = localRules[checkpoint];
    return (
      local?.campaign_id !== rule.campaign_id ||
      local?.is_active !== rule.is_active
    );
  };

  const anyDirty = CHECKPOINT_ORDER.some(isDirty);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-5">
        <div className="max-w-4xl mx-auto flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <MapPin size={20} className="text-[#0a2d4d]" />
              <h1 className="text-xl font-bold text-gray-900">Enrollment Rules</h1>
            </div>
            <p className="text-sm text-gray-500">
              Map each MBBS Lighthouse checkpoint to a campaign. When leads are synced, they auto-enroll into the campaign matching their latest checkpoint.
            </p>
          </div>
          <button
            onClick={handleSaveAll}
            disabled={!anyDirty || saving === 'all'}
            className="flex items-center gap-2 px-4 py-2 bg-[#0a2d4d] text-white text-sm font-medium rounded-lg hover:bg-[#0d3a63] disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
          >
            {saving === 'all' ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Save size={15} />
            )}
            Save All
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-6 space-y-4">
        {/* Info banner */}
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <Info size={16} className="text-blue-600 mt-0.5 shrink-0" />
          <p className="text-sm text-blue-700">
            <strong>How it works:</strong> When a lead is downloaded from the MBBS Lighthouse API, the system checks which checkpoints they have reached. The lead is enrolled into the campaign mapped to their <strong>latest checkpoint</strong> (most recent timestamp). If no rule is active or no campaign is mapped, the lead is synced without enrollment.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-3">
            {CHECKPOINT_ORDER.map((checkpoint) => {
              const meta = CHECKPOINT_META[checkpoint];
              const local = localRules[checkpoint];
              const dirty = isDirty(checkpoint);
              const isSavingThis = saving === checkpoint;

              return (
                <div
                  key={checkpoint}
                  className={`bg-white rounded-xl border ${dirty ? 'border-amber-300 shadow-sm' : 'border-gray-200'} transition-all`}
                >
                  <div className="p-5">
                    <div className="flex items-start gap-4">
                      {/* Checkpoint badge */}
                      <div className={`px-3 py-1.5 rounded-lg border text-xs font-bold uppercase tracking-wider shrink-0 ${meta.bg} ${meta.color}`}>
                        {meta.label}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-500 mb-3">{meta.description}</p>

                        <div className="flex items-center gap-3 flex-wrap">
                          {/* Campaign selector */}
                          <div className="flex-1 min-w-[200px]">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Enroll into Campaign
                            </label>
                            <select
                              value={local?.campaign_id ?? ''}
                              onChange={(e) => handleCampaignChange(checkpoint, e.target.value)}
                              disabled={!local?.is_active}
                              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#0a2d4d]/20 focus:border-[#0a2d4d] disabled:bg-gray-50 disabled:text-gray-400"
                            >
                              <option value="">— No campaign (skip enrollment) —</option>
                              {campaigns.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Active toggle */}
                          <div className="flex items-center gap-2 shrink-0 mt-4">
                            <button
                              onClick={() => handleToggle(checkpoint)}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                                local?.is_active ? 'bg-[#0a2d4d]' : 'bg-gray-300'
                              }`}
                              aria-label={`Toggle ${meta.label} rule`}
                            >
                              <span
                                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                                  local?.is_active ? 'translate-x-4' : 'translate-x-1'
                                }`}
                              />
                            </button>
                            <span className="text-xs text-gray-500">
                              {local?.is_active ? 'Active' : 'Disabled'}
                            </span>
                          </div>
                        </div>

                        {/* Last enrolled timestamp */}
                        {(() => {
                          const rule = rules.find((r) => r.checkpoint === checkpoint);
                          return rule?.last_enrolled_at ? (
                            <div className="flex items-center gap-1.5 mt-3">
                              <Clock size={12} className="text-gray-400 shrink-0" />
                              <span className="text-xs text-gray-400">
                                Last enrolled:{' '}
                                <span className="text-gray-600 font-medium">
                                  {new Date(rule.last_enrolled_at).toLocaleString('en-IN', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true,
                                  })}
                                </span>
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 mt-3">
                              <Clock size={12} className="text-gray-300 shrink-0" />
                              <span className="text-xs text-gray-400">Last enrolled: Never</span>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Status + Save */}
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        {dirty ? (
                          <span className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                            <AlertCircle size={12} />
                            Unsaved
                          </span>
                        ) : local?.campaign_id ? (
                          <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                            <CheckCircle2 size={12} />
                            Saved
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Not mapped</span>
                        )}

                        <button
                          onClick={() => handleSave(checkpoint)}
                          disabled={!dirty || isSavingThis}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#0a2d4d] text-white rounded-lg hover:bg-[#0d3a63] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          {isSavingThis ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <Save size={12} />
                          )}
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Summary card */}
        {!loading && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <RefreshCw size={14} className="text-gray-400" />
              Current Mapping Summary
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {CHECKPOINT_ORDER.map((checkpoint) => {
                const meta = CHECKPOINT_META[checkpoint];
                const rule = rules.find((r) => r.checkpoint === checkpoint);
                return (
                  <div key={checkpoint} className="flex items-center gap-2">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${meta.bg} ${meta.color}`}>
                      {meta.label}
                    </span>
                    <span className="text-xs text-gray-500 truncate">
                      {rule?.campaign_name && rule.is_active
                        ? rule.campaign_name
                        : rule?.is_active
                        ? 'Not mapped' :'Disabled'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
